import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Building2, Search, Loader2, CheckCircle2, XCircle,
  Plus, Sparkles, Trash2, MailPlus, Users
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
};

const AdminInstitutionReviews = () => {
  const navigate = useNavigate();
  const [doctors, setDoctors] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [assignDoctor, setAssignDoctor] = useState(null);
  const [assignInstitutionId, setAssignInstitutionId] = useState("");
  const [assignMakePrimary, setAssignMakePrimary] = useState(true);
  const [createNewName, setCreateNewName] = useState("");
  const [createNewDomains, setCreateNewDomains] = useState("");
  const [assignMode, setAssignMode] = useState("existing"); // existing | create
  const [assignLoading, setAssignLoading] = useState(false);

  const [dismissDoctor, setDismissDoctor] = useState(null);
  const [dismissReason, setDismissReason] = useState("keep_as_lead");
  const [dismissLoading, setDismissLoading] = useState(false);

  const [showInvite, setShowInvite] = useState(false);
  const [inviteInstitutionId, setInviteInstitutionId] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteMakePrimary, setInviteMakePrimary] = useState(true);
  const [inviteLoading, setInviteLoading] = useState(false);

  const loadReviews = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/pending-institution-reviews`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setDoctors(data.doctors || []);
    } catch (e) {
      toast.error("Error al cargar solicitudes");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadInstitutions = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/institutions`, { headers: authHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      const list = Array.isArray(data) ? data : (data.institutions || []);
      setInstitutions(list.filter(i => !i.is_private_practice));
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    loadReviews();
    loadInstitutions();
  }, [loadReviews, loadInstitutions]);

  const openAssign = (doctor) => {
    setAssignDoctor(doctor);
    const first = doctor.institution_suggestions?.[0];
    setAssignInstitutionId(first?.id || "");
    setAssignMode(first ? "existing" : "create");
    setCreateNewName(first ? "" : (doctor.declared_institution || ""));
    setCreateNewDomains("");
    setAssignMakePrimary(true);
  };

  const submitAssign = async () => {
    if (!assignDoctor) return;
    const body = { make_primary: assignMakePrimary };
    if (assignMode === "existing") {
      if (!assignInstitutionId) return toast.error("Elegí una institución");
      body.institution_id = assignInstitutionId;
    } else {
      if (!createNewName.trim()) return toast.error("Nombre de institución requerido");
      body.create_new = {
        name: createNewName.trim(),
        allowed_email_domains: createNewDomains
          .split(",").map(s => s.trim().toLowerCase()).filter(Boolean),
      };
    }
    setAssignLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/doctors/${assignDoctor.id}/assign-institution`,
        { method: 'POST', headers: authHeaders(), body: JSON.stringify(body) }
      );
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || "Institución asignada");
        setAssignDoctor(null);
        loadReviews();
        loadInstitutions();
      } else {
        toast.error(data.detail || "Error al asignar");
      }
    } catch (e) {
      toast.error("Error de conexión");
    } finally {
      setAssignLoading(false);
    }
  };

  const submitDismiss = async () => {
    if (!dismissDoctor) return;
    setDismissLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/doctors/${dismissDoctor.id}/dismiss-institution-request`,
        { method: 'POST', headers: authHeaders(), body: JSON.stringify({ reason: dismissReason }) }
      );
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || "Declaración descartada");
        setDismissDoctor(null);
        loadReviews();
      } else {
        toast.error(data.detail || "Error");
      }
    } catch (e) {
      toast.error("Error de conexión");
    } finally {
      setDismissLoading(false);
    }
  };

  const submitInvite = async () => {
    if (!inviteInstitutionId) return toast.error("Elegí una institución");
    if (!inviteEmail.trim()) return toast.error("Email requerido");
    setInviteLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/institutions/${inviteInstitutionId}/invite-doctor`,
        {
          method: 'POST', headers: authHeaders(),
          body: JSON.stringify({ email: inviteEmail.trim().toLowerCase(), make_primary: inviteMakePrimary }),
        }
      );
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || "Invitación enviada");
        setShowInvite(false);
        setInviteEmail("");
      } else {
        toast.error(data.detail || "Error al invitar");
      }
    } catch (e) {
      toast.error("Error de conexión");
    } finally {
      setInviteLoading(false);
    }
  };

  const filtered = doctors.filter(d => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      d.email?.toLowerCase().includes(s) ||
      d.declared_institution?.toLowerCase().includes(s) ||
      d.name?.toLowerCase().includes(s)
    );
  });

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/doctors')} className="mb-2" data-testid="back-to-doctors-btn">
              <ArrowLeft className="h-4 w-4 mr-2" /> Volver a médicos
            </Button>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Building2 className="h-6 w-6" /> Solicitudes de institución
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Médicos con email genérico que declararon trabajar para una institución.
              Validá su identidad + matrícula antes de asignarles el tenant.
            </p>
          </div>
          <Button onClick={() => setShowInvite(true)} data-testid="open-invite-doctor-btn">
            <MailPlus className="h-4 w-4 mr-2" /> Invitar médico a un tenant
          </Button>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Buscar por email o institución declarada..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-slate-400" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-lg border border-slate-200 p-12 text-center" data-testid="empty-state">
            <Users className="h-10 w-10 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">No hay solicitudes pendientes de revisión.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(d => (
              <div key={d.id} className="bg-white rounded-lg border border-slate-200 p-4" data-testid={`review-card-${d.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-slate-900 truncate">{d.email}</p>
                      <Badge variant="outline" className="text-xs">{d.doctor_status}</Badge>
                    </div>
                    <p className="text-sm text-slate-600">
                      Declaró: <span className="font-semibold text-slate-900">{d.declared_institution}</span>
                    </p>
                    {d.first_name && (
                      <p className="text-xs text-slate-500 mt-1">
                        {d.first_name} {d.last_name} — {d.specialty || 'sin especialidad'} — Mat: {d.license_number || 'pendiente'}
                      </p>
                    )}

                    {d.institution_suggestions?.length > 0 && (
                      <div className="mt-2 flex items-center gap-1.5 text-xs">
                        <Sparkles className="h-3.5 w-3.5 text-blue-600" />
                        <span className="text-slate-500">Coincidencias:</span>
                        {d.institution_suggestions.map(s => (
                          <Badge key={s.id} variant="secondary" className="text-xs">{s.name}</Badge>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2 flex-shrink-0">
                    <Button size="sm" onClick={() => openAssign(d)} data-testid={`assign-btn-${d.id}`}>
                      <CheckCircle2 className="h-4 w-4 mr-1.5" /> Asignar tenant
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setDismissDoctor(d)} data-testid={`dismiss-btn-${d.id}`}>
                      <XCircle className="h-4 w-4 mr-1.5" /> Descartar
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Assign Modal */}
      <Dialog open={!!assignDoctor} onOpenChange={o => !o && setAssignDoctor(null)}>
        <DialogContent data-testid="assign-modal">
          <DialogHeader>
            <DialogTitle>Asignar tenant</DialogTitle>
            <DialogDescription>
              El médico declaró: <strong>{assignDoctor?.declared_institution}</strong>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex gap-2">
              <Button
                variant={assignMode === "existing" ? "default" : "outline"}
                size="sm" className="flex-1"
                onClick={() => setAssignMode("existing")}
                data-testid="mode-existing-btn"
              >
                Institución existente
              </Button>
              <Button
                variant={assignMode === "create" ? "default" : "outline"}
                size="sm" className="flex-1"
                onClick={() => setAssignMode("create")}
                data-testid="mode-create-btn"
              >
                <Plus className="h-4 w-4 mr-1" /> Crear nueva
              </Button>
            </div>

            {assignMode === "existing" ? (
              <div>
                <Label>Elegí una institución existente</Label>
                <select
                  className="w-full mt-1 border border-slate-200 rounded-md px-3 py-2 text-sm"
                  value={assignInstitutionId}
                  onChange={e => setAssignInstitutionId(e.target.value)}
                  data-testid="existing-institution-select"
                >
                  <option value="">— elegir —</option>
                  {institutions.map(i => (
                    <option key={i.id} value={i.id}>{i.name}</option>
                  ))}
                </select>
              </div>
            ) : (
              <>
                <div>
                  <Label htmlFor="new-name">Nombre de la nueva institución</Label>
                  <Input
                    id="new-name"
                    value={createNewName}
                    onChange={e => setCreateNewName(e.target.value)}
                    placeholder="Ej: Sanatorio Santa Fe"
                    data-testid="new-institution-name"
                  />
                </div>
                <div>
                  <Label htmlFor="new-domains">Dominios corporativos (opcional, separados por coma)</Label>
                  <Input
                    id="new-domains"
                    value={createNewDomains}
                    onChange={e => setCreateNewDomains(e.target.value)}
                    placeholder="sanatoriosantafe.com, ssf.org.ar"
                    data-testid="new-institution-domains"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Los médicos con estos dominios se auto-provisionarán en el futuro.
                  </p>
                </div>
              </>
            )}

            <div className="flex items-center gap-2">
              <Checkbox
                id="make-primary"
                checked={assignMakePrimary}
                onCheckedChange={setAssignMakePrimary}
                data-testid="make-primary-checkbox"
              />
              <Label htmlFor="make-primary" className="text-sm cursor-pointer">
                Establecer como tenant principal del médico
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDoctor(null)}>Cancelar</Button>
            <Button onClick={submitAssign} disabled={assignLoading} data-testid="confirm-assign-btn">
              {assignLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Asignar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dismiss Modal */}
      <Dialog open={!!dismissDoctor} onOpenChange={o => !o && setDismissDoctor(null)}>
        <DialogContent data-testid="dismiss-modal">
          <DialogHeader>
            <DialogTitle>Descartar declaración</DialogTitle>
            <DialogDescription>
              El médico quedará solo con Consultorio Privado. Podés registrar
              su institución como lead comercial.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Declaró: <strong>{dismissDoctor?.declared_institution}</strong>
            </p>
            <div>
              <Label>Motivo</Label>
              <select
                className="w-full mt-1 border border-slate-200 rounded-md px-3 py-2 text-sm"
                value={dismissReason}
                onChange={e => setDismissReason(e.target.value)}
                data-testid="dismiss-reason-select"
              >
                <option value="keep_as_lead">Guardar como lead comercial</option>
                <option value="not_our_client">Institución sin acuerdo activo</option>
                <option value="incorrect">Declaración incorrecta / inválida</option>
                <option value="not_specified">Sin motivo específico</option>
              </select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDismissDoctor(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={submitDismiss} disabled={dismissLoading} data-testid="confirm-dismiss-btn">
              {dismissLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="h-4 w-4 mr-1.5" /> Descartar</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Modal */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent data-testid="invite-modal">
          <DialogHeader>
            <DialogTitle>Invitar médico a un tenant</DialogTitle>
            <DialogDescription>
              Útil para médicos con email personal que trabajan en una institución cliente.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label>Institución</Label>
              <select
                className="w-full mt-1 border border-slate-200 rounded-md px-3 py-2 text-sm"
                value={inviteInstitutionId}
                onChange={e => setInviteInstitutionId(e.target.value)}
                data-testid="invite-institution-select"
              >
                <option value="">— elegir —</option>
                {institutions.map(i => (
                  <option key={i.id} value={i.id}>{i.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="invite-email">Email del médico</Label>
              <Input
                id="invite-email"
                type="email"
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                placeholder="medico@ejemplo.com"
                data-testid="invite-email-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="invite-primary"
                checked={inviteMakePrimary}
                onCheckedChange={setInviteMakePrimary}
                data-testid="invite-make-primary-checkbox"
              />
              <Label htmlFor="invite-primary" className="text-sm cursor-pointer">
                Establecer como tenant principal
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvite(false)}>Cancelar</Button>
            <Button onClick={submitInvite} disabled={inviteLoading} data-testid="confirm-invite-btn">
              {inviteLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Invitar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminInstitutionReviews;
